import {
  add,
  format,
  isAfter,
  isBefore,
  startOfDay,
} from "date-fns";

import { UserType, usHolidays } from "../../../utils/constants";
import {
  resolveHolidayRulesToBlockedDates,
  calendarDayKey,
  coerceFormArrayField,
} from "../../../utils/holidayRules";
import {
  getYardCalendarDayOfWeek,
  getYardNow,
  isSameYardScheduleDay,
  setDayFromWorkdayTimeString,
} from "../../../utils/dateUtils";
import { logError } from "../../../utils/appUtils";

const clampSchedulingWindowDays = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return 30;
  }
  return Math.min(365 * 5, Math.max(1, Math.floor(n)));
};

// Fallback for event types whose schedule has missing/short workdays —
// treat unknown days as closed instead of crashing on `.selected`.
const getScheduleWorkday = (eventType, date) =>
  eventType?.schedule?.workdays?.[getYardCalendarDayOfWeek(date)] || {
    selected: false,
  };

export const isWorkday = (evtType, date) =>
  getScheduleWorkday(evtType, date).selected;

export const isDateAfterMaxSchedulingDay = (evtType, day) =>
  isAfter(day, evtType.maxScheduleDay);

export const isDateBeforeMinSchedulingDay = (evtType, day) =>
  isSameYardScheduleDay(day, evtType.minScheduleDay)
    ? false
    : isBefore(day, evtType.minScheduleDay);

export const isHoliday = (evtType, date) =>
  evtType.holidayDayKeySet?.has(calendarDayKey(date)) ?? false;

// "Override Validation" drops every scheduling guard at once — past days,
// holidays, notice windows, closed days and slot capacity — so it is a
// manager-only control. Supervisors and crew (and every customer/carrier user)
// book inside the yard's rules. ADMIN is manager-equivalent here, as it is in
// UserContext's isAuthorized.
export const canOverrideScheduling = () =>
  window.appUser?.type === UserType.MANAGER ||
  window.appUser?.type === UserType.ADMIN;

const isPastSchedulingDay = (day) =>
  isBefore(startOfDay(day), startOfDay(getYardNow()));

export const shouldDisableDate = (evtType, day, override) => {
  if (!evtType) return true;
  // Override wins over the past-day guard so yard staff can backfill
  // appointments (e.g. record yesterday's arrival).
  if (override) return false;
  if (isPastSchedulingDay(day)) return true;
  const workday = isWorkday(evtType, day);
  const beforeMinDay = isDateBeforeMinSchedulingDay(evtType, day);
  const afterMaxDay = isDateAfterMaxSchedulingDay(evtType, day);
  const isYardHoliday = isHoliday(evtType, day);
  return !workday || beforeMinDay || afterMaxDay || isYardHoliday;
};

export const calculateHolidays = (eventType, override) => {
  const clearHolidays = () => {
    eventType.holidays = [];
    eventType.holidayDayKeySet = new Set();
  };

  if (override) {
    clearHolidays();
    return;
  }
  const yardSchedule = window.appUser?.reloadYard?.schedule;
  if (!yardSchedule) {
    clearHolidays();
    return;
  }
  const today = getYardNow();
  const windowStart = startOfDay(today);
  const maxDays = clampSchedulingWindowDays(
    eventType.schedule?.maxDaysIntoFuture ??
      yardSchedule.maxDaysIntoFuture ??
      30
  );
  const windowEnd = add(today, { days: maxDays });
  const holidayStrings = coerceFormArrayField(yardSchedule.holidays);
  try {
    eventType.holidays = resolveHolidayRulesToBlockedDates(
      holidayStrings,
      windowStart,
      windowEnd,
      usHolidays
    );
    eventType.holidayDayKeySet = new Set(
      eventType.holidays.map((d) => calendarDayKey(d))
    );
  } catch (err) {
    logError("SchedulerUtils:calculateHolidays", err, undefined);
    clearHolidays();
  }
};

const getWorkdayStartTime = (date, workDay) =>
  workDay?.startDayTime
    ? setDayFromWorkdayTimeString(date, workDay.startDayTime)
    : date;

const getWorkdayEndTime = (date, workDay) =>
  workDay?.endDayTime
    ? setDayFromWorkdayTimeString(date, workDay.endDayTime)
    : date;

const getSlotMinutes = (eventType) =>
  Number(eventType?.duration || 0) + Number(eventType?.bufferTime || 0);

// Mirror of getDayTimeslots' walk: the grid starts at the workday start, steps
// one slot at a time, and skips every start earlier than minScheduleDay. Once
// the last of those starts is behind us the day offers nothing, so
// minScheduleDay must not land there — the picker would open on an empty grid
// with "Date unavailable" instead of the next bookable day. Overrunning
// endDayTime is not checked here because the grid doesn't check it either: a
// start before the end is offered whole.
const hasTimeslotAtOrAfter = (provisional, workDay, slotMinutes) => {
  if (!(slotMinutes > 0)) return true;
  const dayEnd = getWorkdayEndTime(provisional, workDay);
  let start = getWorkdayStartTime(provisional, workDay);
  while (start < dayEnd) {
    if (start >= provisional) return true;
    start = add(start, { minutes: slotMinutes });
  }
  return false;
};

export const getMinScheduleDay = (eventType, override) => {
  const now = getYardNow();
  let provisional = now;

  if (override) {
    // If override is enabled, ignore minNoticeHours and start today current time, even if after hours.
    const nowWorkDay = getScheduleWorkday(eventType, now);
    const dayStart = getWorkdayStartTime(now, nowWorkDay);
    const dayEnd = getWorkdayEndTime(now, nowWorkDay);

    if (now < dayStart) {
      // If current time is before today's work start, use start time
      provisional = dayStart;
    } else if (now > dayEnd) {
      // If current time is after today's work end, move to the next day and use its start time.
      const nextDay = add(now, { days: 1 });
      const nextWorkDay = getScheduleWorkday(eventType, nextDay);
      provisional = getWorkdayStartTime(nextDay, nextWorkDay);
    }
  } else {
    // Else (if now is within working hours), keep it unchanged.

    // First, add the minNoticeHours to get the provisional time.
    provisional = add(now, { hours: eventType.schedule.minNoticeHours });
    const slotMinutes = getSlotMinutes(eventType);

    // Now, adjust provisional until it falls on a valid workday and within
    // workday hours. Bounded: a schedule with no selected workdays (or all
    // holidays) would otherwise loop forever.
    for (let guard = 0; guard < 370; guard += 1) {
      // If the provisional day is a holiday, skip to the next day.
      if (isHoliday(eventType, provisional)) {
        provisional = add(provisional, { days: 1 });
        // After moving a day forward, reset to the new day’s workday start if available.
        const newWorkDay = getScheduleWorkday(eventType, provisional);
        if (newWorkDay && newWorkDay.selected) {
          provisional = getWorkdayStartTime(provisional, newWorkDay);
        }
        continue;
      }

      // Get the workday for the current provisional day.
      const workDay = getScheduleWorkday(eventType, provisional);
      // If that day isn’t selected, skip to the next day.
      if (!workDay.selected) {
        provisional = add(provisional, { days: 1 });
        const newWorkDay = getScheduleWorkday(eventType, provisional);
        if (newWorkDay && newWorkDay.selected) {
          provisional = getWorkdayStartTime(provisional, newWorkDay);
        }
        continue;
      }

      // Get the start time for that day.
      const dayStart = getWorkdayStartTime(provisional, workDay);

      // If the provisional time is before the day's start, reset it to dayStart.
      if (provisional < dayStart) {
        provisional = dayStart;
        break; // It now falls within the workday.
      }

      // If no slot is left on this day, skip to the next one. Being past
      // dayEnd is only the plainest case: the last slot start comes a slot
      // length before it, so the hour before closing has no bookable start
      // either.
      if (!hasTimeslotAtOrAfter(provisional, workDay, slotMinutes)) {
        provisional = add(provisional, { days: 1 });
        const newWorkDay = getScheduleWorkday(eventType, provisional);
        if (newWorkDay && newWorkDay.selected) {
          provisional = getWorkdayStartTime(provisional, newWorkDay);
        }
        continue;
      }

      // Otherwise, the provisional time is within valid workday hours.
      break;
    }
  }

  return provisional;
};

export const getDayTimeslots = (day, eventType, override) => {
  const timeslots = [];
  const workDay = getScheduleWorkday(eventType, day);
  const slotMinutes = getSlotMinutes(eventType);

  // If the workday is selected or override is enabled, generate timeslots
  if ((workDay.selected || override) && slotMinutes > 0) {
    let startDate = getWorkdayStartTime(day, workDay);

    // Ensure we don't allow times before the min schedule day — unless
    // override is on (its purpose is backfilling past appointments; this
    // clamp would otherwise leave a past day with zero timeslots).
    if (!override) {
      while (startDate < eventType.minScheduleDay) {
        startDate = add(startDate, { minutes: slotMinutes });
      }
    }
    let endDate = getWorkdayEndTime(day, workDay);
    while (startDate < endDate) {
      const slot = format(startDate, "h:mmaaa");
      timeslots.push(slot);

      // Move to the next timeslot
      startDate = add(startDate, { minutes: slotMinutes });
    }
  }

  return timeslots;
};

export const generateDayTimeslots = (
  day,
  eventType,
  override,
  setValidationError
) => {
  const timeslots = getDayTimeslots(day, eventType, override);

  // Handle case where no timeslots are available for a day
  if (timeslots.length === 0) {
    setValidationError("Date unavailable, please select another date.");
  } else {
    setValidationError("");
  }
  return timeslots;
};

/**
 * The day the Scheduler dialog should open on: the appointment being edited
 * when that day is still bookable, otherwise the first available day.
 *
 * A booking in the past, beyond the scheduling window, or on a day the yard has
 * since closed generates no timeslots — getDayTimeslots clamps the grid forward
 * to minScheduleDay — so opening there would show an empty grid and the
 * "Date unavailable" error in place of the appointment. shouldDisableDate covers
 * every one of those cases and compares at day granularity, which a direct
 * compare against minScheduleDay could not: that carries a time of day, while a
 * timeslot's day is midnight. It also yields the right answer under override,
 * where past days are bookable precisely so staff can backfill.
 */
export const resolveSchedulerOpenDay = (
  evtType,
  selectedTimeslot,
  override,
) => {
  if (!evtType) return null;
  // The same guard tryGetDateTimeFromTimeslot applies: a legacy timeslot can
  // carry a time with no calendar parts, and `new Date(NaN, ...)` is an Invalid
  // Date that shouldDisableDate waves through under override (it returns false
  // unconditionally there), handing the picker a NaN day instead of falling
  // back to minScheduleDay.
  const hasCalendarParts =
    Number.isFinite(selectedTimeslot?.year) &&
    Number.isFinite(selectedTimeslot?.month) &&
    Number.isFinite(selectedTimeslot?.day);
  const bookedDay =
    selectedTimeslot?.time &&
    hasCalendarParts &&
    selectedTimeslot.eventTypeId === evtType.id
      ? new Date(
          selectedTimeslot.year,
          selectedTimeslot.month,
          selectedTimeslot.day,
        )
      : null;
  return bookedDay && !shouldDisableDate(evtType, bookedDay, override)
    ? bookedDay
    : evtType.minScheduleDay;
};

export const initializeSchedulingState = (
  selectedTimeslot,
  eventType,
  override,
) => {
  const today = getYardNow();
  let initializedEventType = eventType?.schedule ? { ...eventType } : null;

  if (initializedEventType) {
    calculateHolidays(initializedEventType, override);
    initializedEventType.minScheduleDay = getMinScheduleDay(
      initializedEventType,
      override
    );
    initializedEventType.maxScheduleDay = add(today, {
      days: initializedEventType.schedule?.maxDaysIntoFuture || 30,
    });
  }

  const schedulingData = selectedTimeslot?.time
    ? {
        date: new Date(
          selectedTimeslot.year,
          selectedTimeslot.month,
          selectedTimeslot.day
        ),
        slot: selectedTimeslot.time,
      }
    : {
        date: initializedEventType?.minScheduleDay || today,
        slot: "",
      };

  return { initializedEventType, schedulingData };
};

export const finalizeAppointmentSubmit = (requestId, currentRequestId) => ({
  shouldClearLoading: requestId === currentRequestId,
  shouldClearSubmitting: requestId === currentRequestId,
});

export const getDismissedSchedulerState = (
  currentAvailabilityRequestId,
  currentSubmitRequestId,
) => ({
  nextAvailabilityRequestId: currentAvailabilityRequestId + 1,
  nextSubmitRequestId: currentSubmitRequestId + 1,
  shouldClearLoading: true,
  shouldClearSubmitting: true,
  shouldClearValidationError: true,
  unavailableTimeSlots: {},
});
